// ✅ src/pages/Inventory/LedgerInterReport.jsx  (FULL FRONTEND - UPDATED)
// ASPX: LedgerInterReport.aspx  (INFRA SCREEN : B-52/1)
//
// ✅ Uses SAME design template + UI style you pasted:
// - HeaderBar + VehicleLayoutWrapper + Skin colors (buttonClass / hoverClass)
// - Monthwise / Period radio
// - Month + Year dropdowns (Monthwise)
// - From / To dates (Period) + same validation as Check_from_to()
// - txtValue with autocomplete (ASPX AutoCompleteExtender)
// - Search -> calls backend /list
// - Print -> calls backend /printParams then navigates to Print page with state
// - Grid Slno clickable -> calls backend /rowCommand and opens/redirects based on response
//
// ✅ Backend endpoints expected (from my node route):
//   GET  /api/ledgerInterReport/init?code=SUPP...            (or /meta if you keep old naming)
//   GET  /api/ledgerInterReport/autocomplete?mode=...&q=...
//   GET  /api/ledgerInterReport/list?...filters...
//   GET  /api/ledgerInterReport/rowCommand?code=...&grnno=...
//   GET  /api/ledgerInterReport/printParams?...filters...
//   GET  /api/ledgerInterReport/checkRights?userId=...

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaArrowLeft, FaFilter, FaInfoCircle, FaPrint, FaSearch } from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/ledgerInterReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select" || s.toLowerCase() === "month" || s.toLowerCase() === "year";
};

const pad2 = (n) => String(n).padStart(2, "0");

// expects dd/MM/yyyy -> Date
const parseDDMMYYYY = (ddmmyyyy) => {
  const s = toStr(ddmmyyyy);
  if (!s) return null;
  const parts = s.split("/");
  if (parts.length !== 3) return null;

  const dd = Number(parts[0]);
  const mm = Number(parts[1]);
  const yy = Number(parts[2]);

  if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(yy)) return null;
  if (yy < 1900 || mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;

  const d = new Date(yy, mm - 1, dd, 0, 0, 0, 0);
  if (Number.isNaN(d.getTime())) return null;

  // strict check (avoid 31/02)
  if (d.getFullYear() !== yy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return null;
  return d;
};

const fmtDDMMYYYY = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const monthAgoDDMMYYYY = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return fmtDDMMYYYY(d);
};

const todayDDMMYYYY = () => fmtDDMMYYYY(new Date());

const getQueryCode = (search) => {
  const qs = new URLSearchParams(search);

  // preferred: ?code=SUPPxxxx
  const code = qs.get("code");
  if (code) return code;

  // ASPX style: Request.QueryString[0] (single unnamed param)
  // ex: ?SUPP0001  => entries: [["SUPP0001",""]]
  const entries = Array.from(qs.entries());
  if (entries.length === 1) {
    const k = entries[0][0];
    const v = entries[0][1];
    if (k && (!v || v === "")) return k;
  }
  return "";
};

export default function LedgerInterReport() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const code = useMemo(() => getQueryCode(search), [search]);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // match your other pages
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchType = localStorage.getItem("branchtype") || "HO";
  const branchId = Number(localStorage.getItem("branchid") || 0);

  // ledgerBranch used by supplier SP in old ASPX (Session["ledgerBranch"])
  const ledgerBranch = localStorage.getItem("ledgerBranch") || localStorage.getItem("branchid") || "";

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // mode: MONTHWISE | PERIOD (ASPX default Period checked true)
  const [mode, setMode] = useState("PERIOD");

  // dropdown meta
  const [months, setMonths] = useState([{ label: "Select", value: "Select" }]);
  const [years, setYears] = useState([{ label: "Select", value: "Select" }]);

  // labels (ASPX lblRptForHeading, lblRptFor, lblRptType)
  const [uiLabels, setUiLabels] = useState({
    rptForHeading: "Report For",
    rptFor: "",
    rptType: "Value",
  });

  // supplier name heading (lbl_suppliername)
  const [supplierTitle, setSupplierTitle] = useState("");

  // codeType + autocompleteMode from backend meta
  const [codeType, setCodeType] = useState("");
  const [autocompleteMode, setAutocompleteMode] = useState(""); // voucher|type|itemcode

  // filters
  const [filters, setFilters] = useState({
    month: "Select",
    year: "Select",
    fromDate: monthAgoDDMMYYYY(),
    toDate: todayDDMMYYYY(),
    value: "", // txtValue
  });

  // autocomplete
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const acTimer = useRef(null);

  // result grid
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState(""); // lbl_Message

  const inputClass = useMemo(() => {
    return `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
      isDark
        ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
        : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
    }`;
  }, [isDark]);

  const labelClass = useMemo(() => {
    return `block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`;
  }, [isDark]);

  /* ---------------- ASPX-like validation (Check_from_to) ---------------- */
  const validateDates = useCallback(() => {
    if (mode !== "PERIOD") return true;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const from = parseDDMMYYYY(filters.fromDate);
    const to = parseDDMMYYYY(filters.toDate);

    if (filters.fromDate && !from) {
      toast.error("Invalid From date (use dd/MM/yyyy)");
      return false;
    }
    if (filters.toDate && !to) {
      toast.error("Invalid To date (use dd/MM/yyyy)");
      return false;
    }

    if (from && from > today) {
      toast.error("Select from date lesser than current date");
      return false;
    }
    if (to && to > today) {
      toast.error("Select to date lesser than current date");
      return false;
    }
    if (from && to && to < from) {
      toast.error("Please select from date less than to date");
      return false;
    }
    return true;
  }, [mode, filters.fromDate, filters.toDate]);

  /* ---------------- API: check rights (optional) ---------------- */
  const checkRights = useCallback(async () => {
    try {
      // if you want to enforce rights like ASPX, keep it
      const r = await axios.get(`${API_BASE}/checkRights`, { params: { userId } });
      if (r?.data?.success && r.data.hasRights === false) {
        toast.error("You dont have rights to access this page");
        navigate("/inventory/InventoryHome", { replace: true });
      }
      // print rights: you can hide clickable slno if hasPrintRights=false
    } catch {
      // ignore (some DBs may not have rights SPs)
    }
  }, [userId, navigate]);

  /* ---------------- API: load init/meta ---------------- */
  const loadMeta = useCallback(async () => {
    if (!code) return;

    setLoading(true);
    try {
      // uses /init (from node code). If you named it /meta, just change URL here.
      const res = await axios.get(`${API_BASE}/meta`, {
        params: {
          code,
          userId,
          branchType,
          branchId,
          ledgerBranch,
          periodMode: mode, // just for defaults (server may ignore)
        },
      });

      const data = res.data || {};
      if (!data.success) throw new Error(data.message || "Init failed");

      // months/years
      setMonths(data.months || [{ label: "Select", value: "Select" }]);
      setYears(data.years || [{ label: "Select", value: "Select" }]);

      // supplier heading
      setSupplierTitle(data.supplierName || "");

      // labels
      setUiLabels({
        rptForHeading: data?.labels?.rptForHeading || "Report For",
        rptFor: data?.code || code,
        rptType: data?.labels?.rptTypeLabel || "Value",
      });

      setCodeType(data.codeType || "");
      setAutocompleteMode(data.autocompleteMode || "");

      // default dates
      if (data?.defaults) {
        setMode(String(data.defaults.periodMode || "PERIOD").toUpperCase());
        setFilters((p) => ({
          ...p,
          fromDate: data.defaults.fromDate || p.fromDate,
          toDate: data.defaults.toDate || p.toDate,
        }));
      }
    } catch (e) {
      toast.error(e?.message || "Failed to load page meta");
    } finally {
      setLoading(false);
    }
  }, [code, userId, branchType, branchId, ledgerBranch, mode]);

  /* ---------------- API: autocomplete ---------------- */
  const fetchSuggestions = useCallback(
    async (prefix) => {
      const p = toStr(prefix);
      if (!p || p.length < 1) {
        setSuggestions([]);
        return;
      }

      if (!autocompleteMode) {
        setSuggestions([]);
        return;
      }

      try {
        const res = await axios.get(`${API_BASE}/autocomplete`, {
          params: { mode: autocompleteMode, q: p },
        });
        setSuggestions(res.data?.items || []);
      } catch {
        setSuggestions([]);
      }
    },
    [autocompleteMode]
  );

  /* ---------------- API: search/list ---------------- */
  const handleSearch = useCallback(async () => {
    setMessage("");

    // txtValue required in ASPX
    if (!toStr(filters.value)) {
      toast.error("Please enter the required value");
      return;
    }

    if (mode === "PERIOD" && !validateDates()) return;

    if (mode === "MONTHWISE") {
      // match ASPX: month/year can default but you asked for same strict UX
      if (isSelect(filters.month) || isSelect(filters.year)) {
        toast.error("Please select Month and Year");
        return;
      }
    }

    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/list`, {
        params: {
          code,
          ledgerBranch,
          reportWise: mode === "MONTHWISE" ? "monthwise" : "period",
          month: filters.month,
          year: filters.year,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          txtValue: filters.value,
        },
      });

      const data = res.data || {};
      if (!data.success) throw new Error(data.message || "Search failed");

      setRows(data.rows || []);
      if (data.message) setMessage(data.message);
      else setMessage("");

      if ((data.rows || []).length === 0) toast.info("No Records Found!");
      else toast.success("Report loaded");
    } catch (e) {
      setRows([]);
      const msg = e?.response?.data?.message || e?.message || "Failed to load report";
      setMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [code, ledgerBranch, mode, filters, validateDates]);

  /* ---------------- Print ---------------- */
  const handlePrint = useCallback(async () => {
    setMessage("");

    if (!toStr(filters.value)) {
      toast.error("Please enter the required value");
      return;
    }

    if (mode === "PERIOD" && !validateDates()) return;

    if (mode === "MONTHWISE") {
      if (isSelect(filters.month) || isSelect(filters.year)) {
        toast.error("Please select Month and Year");
        return;
      }
    }

    try {
      const res = await axios.get(`${API_BASE}/printParams`, {
        params: {
          code,
          reportWise: mode === "MONTHWISE" ? "monthwise" : "period",
          month: filters.month,
          year: filters.year,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          txtValue: filters.value,
        },
      });

      const data = res.data || {};
      if (!data.success) throw new Error(data.message || "Print params failed");

      // Navigate to your print page (create LedgerInterReport_Print.jsx)
      navigate("/inventory/LedgerInterReport_Print", {
        state: {
          // pass everything print needs (same pattern as your other pages)
          userId,
          branchType,
          branchId,
          ledgerBranch,
          code,
          codeType,
          mode,
          supplierTitle,
          filters: { ...filters },
          printParams: data, // supp_code, voucher, frmdate, todate, etc.
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Print failed");
    }
  }, [navigate, userId, branchType, branchId, ledgerBranch, code, codeType, mode, supplierTitle, filters, validateDates]);

  /* ---------------- Row click (ASPX LinkButton CommandName=fld_grnno) ---------------- */
  const handleRowClick = useCallback(
    async (row) => {
      // in supplier ledger SP: field usually fld_grnno (ASPX binds CommandName to fld_grnno)
      const grnNo =
        row?.fld_grnno ??
        row?.GRNNo ??
        row?.grnno ??
        row?.GrnNo ??
        row?.fld_grnNo ??
        "";

      if (!grnNo) {
        toast.info("No GRN/PRTN number found in this row");
        return;
      }

      try {
        const res = await axios.get(`${API_BASE}/rowCommand`, {
          params: {
            code,
            grnno: grnNo,
            ledgerBranch,
            reportWise: mode === "MONTHWISE" ? "monthwise" : "period",
            month: filters.month,
            year: filters.year,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
          },
        });

        const data = res.data || {};
        if (!data.success) throw new Error(data.message || "Open failed");

        const targets = data.openTargets || [];
        if (!targets.length) return;

        // You can map these to React routes or window.open like ASPX did.
        // Here I keep it compatible with your React routing style:
        const t = targets[0];

        if (t.page === "LedgerReport") {
          // your route example:
          window.open(`/inventory/LedgerReport?id=${encodeURIComponent(t.query.id)}`, "_blank");
          return;
        }

        if (t.page === "PurchaseReturn_print") {
          window.open(`/inventory/PurchaseReturn_print?id=${encodeURIComponent(t.query.id)}`, "_blank");
          return;
        }

        if (t.page === "GoodsReceiptNote_print") {
          window.open(`/inventory/GoodsReceiptNote_print?id=${encodeURIComponent(t.query.id)}`, "_blank");
          return;
        }

        // fallback: send to LedgerMainReport
        navigate("/inventory/LedgerMainReport", {
          state: {
            grnNo,
            from: "LedgerInterReport",
            mode,
            filters: { ...filters },
            row,
          },
        });
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || "Failed to open row");
      }
    },
    [code, ledgerBranch, mode, filters, navigate]
  );

  /* ---------------- init ---------------- */
  useEffect(() => {
    if (!code) {
      toast.error("Missing code in URL. Use ?code=SUPPxxxx / DEPTxxxx / VEH/xxxx");
      return;
    }
    checkRights();
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  /* ---------------- handlers ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFilters((p) => ({ ...p, [name]: value }));

    if (name === "value") {
      // debounce
      if (acTimer.current) clearTimeout(acTimer.current);
      acTimer.current = setTimeout(() => {
        fetchSuggestions(value);
        setShowSuggestions(true);
      }, 200);
    }
  };

  const handleSelectSuggestion = (val) => {
    setFilters((p) => ({ ...p, value: val }));
    setShowSuggestions(false);
  };

  const handleModeChange = (newMode) => {
    const m = String(newMode).toUpperCase();
    setMode(m);

    // mimic ASPX: toggle panels
    setRows([]);
    setMessage("");

    // period defaults if switching back
    if (m === "PERIOD") {
      setFilters((p) => ({
        ...p,
        fromDate: p.fromDate || monthAgoDDMMYYYY(),
        toDate: p.toDate || todayDDMMYYYY(),
      }));
    }
  };

  // dynamic columns from server rows (besides slno col)
  const gridColumns = useMemo(() => {
    const first = rows?.[0];
    if (!first) return [];
    return Object.keys(first);
  }, [rows]);

  const SuggestionsDropdown = ({ show, items }) => {
    if (!show || !items?.length) return null;
    return (
      <div
        className={`absolute z-20 w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-auto ${
          isDark ? "bg-gray-800 border-gray-600" : "bg-white border-gray-300"
        }`}
      >
        {items.map((s, idx) => (
          <div
            key={`${s}-${idx}`}
            onClick={() => handleSelectSuggestion(s)}
            className={`px-3 py-2 cursor-pointer ${
              isDark ? "hover:bg-gray-700 text-gray-200" : "hover:bg-gray-100 text-gray-900"
            }`}
          >
            {s}
          </div>
        ))}
      </div>
    );
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">LEDGER SUB REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Top actions */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Ledger Sub Report
                </h1>
                {loading && (
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium animate-pulse ${
                      isDark ? "bg-gray-700 text-gray-200" : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    Loading...
                  </span>
                )}
              </div>

              {/* Like ASPX top-right anchor to LedgerMainReport.aspx */}
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                <button
                  className={`underline hover:no-underline ${isDark ? "text-blue-300" : "text-blue-700"}`}
                  onClick={() => navigate("/inventory/LedgerMainReport")}
                >
                  Ledger Main Report
                </button>
              </div>

              {/* Show what report is for (ASPX lblRptForHeading + lblRptFor) */}
              <div className={`text-xs mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {uiLabels.rptForHeading}: <span className="font-semibold">{uiLabels.rptFor || code}</span>
                {codeType ? <span className="ml-2">({codeType})</span> : null}
              </div>
            </div>

            <div className="flex items-center gap-3 justify-end">
              <div className={`text-right text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                INFRA SCREEN : B-52/1
              </div>

              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handlePrint}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaPrint className="w-4 h-4" />
                Print
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              {/* Monthwise / Period */}
              <div className="flex flex-col md:flex-row gap-6 mb-6">
                <label className={`flex items-center gap-2 cursor-pointer ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  <input
                    type="radio"
                    name="mode"
                    checked={mode === "MONTHWISE"}
                    onChange={() => handleModeChange("MONTHWISE")}
                  />
                  Month wise
                </label>

                <label className={`flex items-center gap-2 cursor-pointer ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  <input
                    type="radio"
                    name="mode"
                    checked={mode === "PERIOD"}
                    onChange={() => handleModeChange("PERIOD")}
                  />
                  Period
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Month + Year */}
                <div>
                  <label className={labelClass}>Monthwise</label>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      name="month"
                      value={filters.month}
                      onChange={handleChange}
                      className={inputClass}
                      disabled={mode !== "MONTHWISE"}
                    >
                      {(months || []).map((m, idx) => (
                        <option key={`${m.value}-${idx}`} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>

                    <select
                      name="year"
                      value={filters.year}
                      onChange={handleChange}
                      className={inputClass}
                      disabled={mode !== "MONTHWISE"}
                    >
                      {(years || []).map((y, idx) => (
                        <option key={`${y.value}-${idx}`} value={y.value}>
                          {y.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {mode !== "MONTHWISE" && (
                    <div className={`text-xs mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Enable “Month wise” to select Month/Year
                    </div>
                  )}
                </div>

                {/* From */}
                <div>
                  <label className={labelClass}>From</label>
                  <input
                    type="text"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="dd/MM/yyyy"
                    disabled={mode !== "PERIOD"}
                    autoComplete="off"
                  />
                  {mode === "PERIOD" && filters.fromDate && parseDDMMYYYY(filters.fromDate) && (
                    <div className={`text-xs mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Selected: {fmtDDMMYYYY(parseDDMMYYYY(filters.fromDate))}
                    </div>
                  )}
                </div>

                {/* To */}
                <div>
                  <label className={labelClass}>To</label>
                  <input
                    type="text"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="dd/MM/yyyy"
                    disabled={mode !== "PERIOD"}
                    autoComplete="off"
                  />
                  {mode === "PERIOD" && filters.toDate && parseDDMMYYYY(filters.toDate) && (
                    <div className={`text-xs mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Selected: {fmtDDMMYYYY(parseDDMMYYYY(filters.toDate))}
                    </div>
                  )}
                </div>

                {/* Report For */}
                <div>
                  <label className={labelClass}>{uiLabels.rptForHeading || "Report For"}</label>
                  <div
                    className={`px-3 py-2 rounded-lg border ${
                      isDark ? "border-gray-700 bg-gray-900/30 text-gray-200" : "border-gray-200 bg-white text-gray-800"
                    }`}
                  >
                    {uiLabels.rptFor || code || "-"}
                  </div>
                </div>

                {/* Required txtValue with autocomplete */}
                <div className="relative">
                  <label className={labelClass}>
                    {uiLabels.rptType || "Value"} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="value"
                    value={filters.value}
                    onChange={handleChange}
                    onFocus={() => {
                      if (filters.value) {
                        fetchSuggestions(filters.value);
                        setShowSuggestions(true);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className={inputClass}
                    placeholder={
                      autocompleteMode === "voucher"
                        ? "Purchase / Purchase return"
                        : autocompleteMode === "type"
                        ? "Purchase Request / STN Request / Store Request"
                        : "Type to search..."
                    }
                    autoComplete="off"
                  />
                  <SuggestionsDropdown show={showSuggestions} items={suggestions} />
                </div>
              </div>

              <div className="flex justify-end items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSearch}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                >
                  <FaSearch />
                  Search
                </button>
              </div>

              {/* message label like ASPX lbl_Message */}
              {message ? <div className="mt-4 text-sm text-red-500 font-medium">{message}</div> : null}
            </div>
          )}

          {/* Supplier label row like ASPX tableheader */}
          {supplierTitle ? (
            <div className={`mb-3 font-bold underline ${isDark ? "text-gray-100" : "text-gray-900"}`}>
              {supplierTitle}
            </div>
          ) : null}

          {/* Grid */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {rows.length === 0 ? (
              <div className="text-center py-12">
                <FaInfoCircle className={`w-12 h-12 mx-auto mb-4 ${isDark ? "text-gray-600" : "text-gray-400"}`} />
                <p className={isDark ? "text-gray-400" : "text-gray-500"}>No Records Found!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${isDark ? "bg-gray-800" : "bg-gray-100"}`}>
                    <tr>
                      <th className="px-4 py-3 text-center text-xs font-semibold w-20">Slno</th>
                      {gridColumns.map((k) => (
                        <th key={k} className="px-4 py-3 text-left text-xs font-semibold whitespace-nowrap">
                          {k}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((row, idx) => (
                      <tr
                        key={row?.id || row?.fld_grnno || idx}
                        className={`${isDark ? "bg-gray-800" : "bg-white"} hover:${isDark ? "bg-gray-700" : "bg-gray-50"} transition-colors`}
                      >
                        {/* clickable slno like ASPX LinkButton */}
                        <td className="px-4 py-3 text-center">
                          <button
                            className={`font-semibold hover:underline ${isDark ? "text-blue-300" : "text-blue-700"}`}
                            onClick={() => handleRowClick(row)}
                            title="Open"
                          >
                            {idx + 1}
                          </button>
                        </td>

                        {gridColumns.map((k) => (
                          <td key={`${k}-${idx}`} className={`px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {row?.[k] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}